import { ReactComponent as WheelThumbnail } from "@assets/images/Wheel-thumbnail.svg";
import { ReactComponent as ClonedIcon } from "@assets/images/clonedBlue.svg";
import DefaultIyugaLogo from "@assets/images/iyuga.jpg";
import { useNode } from "@craftjs/core";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import HTMLEllipsis from "react-lines-ellipsis/lib/html";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { setCheckboxLinkedWheel } from "../../../../redux/actions";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { getWheelDetails } from "../../../../services/api";
import {
  applyOpacity,
  getSuggestedColor,
  isValidSuggestedColor,
  transformedText,
} from "../../../../utils/helpers";
import Modal from "../../../Modal";
import WheelComponent from "../../../WheelComponent";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import LinkWheelSettings from "./children/LinkWheelSettings";
import { styles } from "./styles";
import { updateLinkWheelStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";

const GradientShadowBox = styled(Box)(
  ({ theme, dropShadowColor, borderRadius, borderWidth }) => ({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    height: "140px",
    padding: `${16 - (borderWidth || 0)}px`,
    borderRadius: `${borderRadius}px`,
    cursor: "pointer",
    boxSizing: "border-box",
    "::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      boxShadow: dropShadowColor ? `0 4px 6px -1px ${dropShadowColor}` : "none",
      borderRadius: `${borderRadius}px`,
      zIndex: -1,
      background:
        dropShadowColor &&
        typeof dropShadowColor === "string" &&
        dropShadowColor.startsWith("linear-gradient")
          ? dropShadowColor
          : undefined,
      filter: "blur(8px)",
    },
  })
);

const getBackgroundStyle = (wheelData) => {
  let wallpaper = "#E1E1E1"; // Default color

  if (wheelData) {
    if (
      wheelData.version === 1 &&
      wheelData.wallpaper &&
      wheelData.wallpaper.trim() !== ""
    ) {
      wallpaper = wheelData.wallpaper;
    } else if (
      wheelData.version === 2 &&
      wheelData.wallpaper_settings &&
      wheelData.wallpaper_settings.bg_wallpaper &&
      wheelData.wallpaper_settings.bg_wallpaper.trim() !== ""
    ) {
      wallpaper = wheelData.wallpaper_settings.bg_wallpaper;
    }
  }

  if (
    typeof wallpaper === "string" &&
    wallpaper.startsWith("linear-gradient")
  ) {
    return {
      backgroundImage: wallpaper,
      backgroundColor: undefined,
    };
  } else if (typeof wallpaper === "string" && wallpaper.includes("https")) {
    return {
      backgroundImage: `url(${wallpaper})`,
      backgroundColor: undefined,
    };
  } else {
    return {
      backgroundColor: wallpaper,
      backgroundImage: undefined,
    };
  }
};

export const LinkWheel = ({
  margin,
  isApplyMarginOnAllSides,
  allSideMargin,
  wheelId,
  showDescription,
  borderRadius,
  titleColor,
  textColor,
  backgroundColor,
  backgroundOpacity,
  borderColor,
  borderWidth,
  dropShadowColor,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
    props,
  } = useNode((node) => ({ props: node.data.props }));
  const dispatch = useDispatch();
  const { linkWheel: linkWheelProps } = useSelector(
    (state) => state.codeblockStyles
  );
  const [open, setOpen] = React.useState(false);
  const [checked, setChecked] = useState(false);
  const { checkboxLinkedWheel } = useSelector((state) => state.userProfile);

  const [wheelData, setWheelData] = useState(null);
  const [wheelSegments, setWheelSegments] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();

  const isPrivate = wheelData?.visibility === 2;

  useEffect(() => {
    // Check if borderColor is explicitly null (not just falsy)
    if (
      borderColor === null ||
      borderColor === undefined ||
      borderColor === ""
    ) {
      // Immediately set the border color without delay
      setProp((props) => {
        props.borderColor = linkWheelProps.borderColor || "suggested_color_0";
      });
    }
  }, [borderColor, linkWheelProps.borderColor]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Don't copy wheelId, only styles
        props.margin = props.margin || { ...linkWheelProps.margin };
        props.isApplyMarginOnAllSides =
          props.isApplyMarginOnAllSides ||
          linkWheelProps.isApplyMarginOnAllSides;
        props.allSideMargin =
          props.allSideMargin || linkWheelProps.allSideMargin;
        props.showDescription =
          props.showDescription !== undefined
            ? props.showDescription
            : linkWheelProps.showDescription;
        props.borderRadius = props.borderRadius || linkWheelProps.borderRadius;
        props.titleColor = props.titleColor || linkWheelProps.titleColor;
        props.textColor = props.textColor || linkWheelProps.textColor;
        props.backgroundColor =
          props.backgroundColor || linkWheelProps.backgroundColor;
        props.backgroundOpacity =
          props.backgroundOpacity || linkWheelProps.backgroundOpacity;
        props.borderColor = props.borderColor || linkWheelProps.borderColor;
        props.borderWidth = props.borderWidth || linkWheelProps.borderWidth;
        props.dropShadowColor =
          props.dropShadowColor || linkWheelProps.dropShadowColor;
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, [linkWheelProps]);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.LinkWheel);
  // One load only. `wheelId` (Craft prop) and `props.wheelId` are the same
  // id — two effects used to fire two `?preview=true` 404 toasts on a bad
  // Embed URL, stacked with LinkWheelSettings' own fetch.
  const resolvedWheelId = wheelId || props.wheelId;
  useEffect(() => {
    if (resolvedWheelId) getWheelData(resolvedWheelId);
  }, [resolvedWheelId]);

  const getWheelData = async (id) => {
    setLoading(true);
    try {
      // Fetch wheel details with preview=true query parameter
      const res = await getWheelDetails(id, "?preview=true");
      if (res?.data?.data) {
        const response = res.data.data;
        const modifiedSegmentData = response.wheel_segments.map((s) => {
          const { nodes, ...rest } = s;
          // const { activity, category, ...settingsRest } = s.settings;
          return {
            ...rest,
            category: 1, // This will replace with actual category id
            // settings: convertSnakeToCamel(settingsRest),
            nodes: typeof nodes == "string" ? JSON.parse(nodes) : nodes,
          };
        });
        setWheelData(response);
        setWheelSegments(modifiedSegmentData);
      }
    } catch (error) {
      toast.error(error.message, { toastId: `link-wheel-${id}` });
    } finally {
      setLoading(false);
    }
  };

  const skeletonHeights = [16, 16, 12, 12, 8, 8];

  const { backgroundColor: contentBgColor, backgroundImage: contentBgImage } =
    getBackgroundStyle(wheelData);

  const nav = useNavigate();

  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  const handleNavigateToViewWheel = () => {
    if (!checkboxLinkedWheel) handleOpen();
    else {
      handleViewWheel();
    }
  };

  const handleCheckboxChange = (event) => {
    setChecked(event.target.checked);
  };

  const clonedWheelData = wheelSegments?.map((segment) => ({
    ...segment,
    ...segment?.settings,
  }));

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  // Always use a valid border color, falling back to the default if necessary
  const validBorderColor =
    borderColor || linkWheelProps.borderColor || "suggested_color_0";
  const border = `${borderWidth || 1}px solid ${getPaletteColor(
    validBorderColor
  )}`;

  const handleViewWheel = () => {
    if (checked) dispatch(setCheckboxLinkedWheel(true));
    if (wheelData && wheelData.id) {
      window.open(`/wheel/${wheelData.id}`, "_blank");
      handleClose();
    }
  };

  return (
    <>
      <div ref={(ref) => connect(drag(ref))}>
        <BlockIndicator>
          <GradientShadowBox
            dropShadowColor={getPaletteColor(dropShadowColor)}
            borderRadius={borderRadius}
            borderWidth={borderWidth}
            style={{
              background: wheelData
                ? isValidSuggestedColor(backgroundColor)
                  ? getPaletteColor(backgroundColor)
                  : `${applyOpacity(backgroundColor, backgroundOpacity)}`
                : "#F9FAFB",
              backgroundImage:
                backgroundColor &&
                typeof backgroundColor === "string" &&
                backgroundColor.startsWith("linear-gradient")
                  ? backgroundColor
                  : undefined,
              margin: marginStyle,
              gap: "16px",
              border,
            }}
          >
            <Box
              data-testid="link-wheel-block-icon-slot"
              sx={{
                width: "40%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                height: "108px",
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
                flexShrink: 0,
                boxSizing: "border-box",
                backgroundColor: contentBgColor,
                backgroundImage: contentBgImage,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            >
              {!wheelData && <WheelThumbnail />}

              {wheelData && (
                <div
                  onClick={handleNavigateToViewWheel}
                  style={{
                    width: "100%",
                    height: "100%",
                    maxWidth: "108px",
                    maxHeight: "108px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WheelComponent
                    resize={true}
                    userData={{
                      id: uuidv4(),
                      url:
                        wheelData?.wheel_center_media?.url ||
                        wheelData?.wheel_image ||
                        DefaultIyugaLogo,
                    }}
                    maxSelectedArcs={0}
                    arcThickness={-10}
                    chartData={clonedWheelData}
                    margin={1}
                    width="100%"
                    height="100%"
                    containerDivStyle={{
                      width: "100%",
                      height: "100%",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      overflow: "hidden",
                    }}
                  ></WheelComponent>
                </div>
              )}
            </Box>

            <Box
              sx={{
                width: "60%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              {!wheelData &&
                skeletonHeights.map((height, index) => (
                  <Skeleton
                    key={index}
                    variant="text"
                    height={`${height}px`}
                    sx={{ borderRadius: "2px", mb: "7px" }}
                  />
                ))}
              {wheelData && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "flex-end",
                      gap: "4px",
                      flexShrink: 0,
                      mb: 0.5,
                    }}
                  >
                    {isPrivate && (
                      <Tooltip placement="top" arrow title="Private">
                        <VisibilityOffIcon sx={styles.iconStyles} />
                      </Tooltip>
                    )}

                    {wheelData?.is_locked && (
                      <Tooltip placement="top" arrow title="Locked">
                        <LockIcon
                          sx={{ ...styles.iconStyles, color: "#123051" }}
                        />
                      </Tooltip>
                    )}

                    {wheelData?.is_cloned && (
                      <Tooltip placement="top" arrow title="Cloned">
                        <ClonedIcon style={styles.iconStyles} />
                      </Tooltip>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: "700",
                      ...(showDescription && {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }),
                      flexShrink: 0,
                      fontFamily: "DM Sans",
                      color: getPaletteColor(titleColor),
                      ...(typeof titleColor === "string" &&
                        titleColor.startsWith("linear-gradient") && {
                          background: titleColor,
                          "-webkit-background-clip": "text",
                          "-webkit-text-fill-color": "transparent",
                          display: "inline",
                        }),
                      fontSize: "14px",
                      lineHeight: "20.5px",

                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                    onClick={handleNavigateToViewWheel}
                  >
                    {transformedText(wheelData.wheel_name) || "Untitled Wheel"}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: "400",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flexShrink: 0,
                      fontFamily: "DM Sans",
                      color: getPaletteColor(textColor),
                      ...(typeof textColor === "string" &&
                        textColor.startsWith("linear-gradient") && {
                          background: textColor,
                          "-webkit-background-clip": "text",
                          "-webkit-text-fill-color": "transparent",
                          display: "inline",
                        }),
                      fontSize: "10px",
                      lineHeight: "20.5px",
                      textTransform: "capitalize",
                    }}
                  >
                    By {wheelData.user_profile?.first_name}{" "}
                    {wheelData.user_profile?.last_name}
                  </Typography>

                  {showDescription && (
                    <Typography
                      sx={{
                        //whiteSpace: "nowrap",
                        //overflow: "hidden",
                        //textOverflow: "ellipsis",
                        //whiteSpace: "normal",
                        fontWeight: "400",
                        fontFamily: "DM Sans",
                        color: getPaletteColor(textColor),
                        ...(typeof textColor === "string" &&
                          textColor.startsWith("linear-gradient") && {
                            background: textColor,
                            "-webkit-background-clip": "text",
                            "-webkit-text-fill-color": "transparent",
                            display: "inline",
                          }),
                        fontSize: "10px",
                        lineHeight: "12px",
                      }}
                      component="div"
                    >
                      <HTMLEllipsis
                        unsafeHTML={
                          transformedText(wheelData.description) || "-"
                        }
                        maxLine="3"
                        ellipsis="..."
                        //trimRight
                        basedOn="letters"
                      />
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </GradientShadowBox>
        </BlockIndicator>
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        message={
          "You are about to be directed away to another Wheel. Are you sure?"
        }
        primaryButtonText={"Navigate"}
        secondaryButtonText={"Cancel"}
        handleSecondaryButton={handleClose}
        handleSubmit={handleViewWheel}
        title="Navigating to Wheel View"
        padding="32px 60px"
        height="170px"
        borderRadius={"7px"}
        isCheckbox
        onCheckboxChange={handleCheckboxChange}
      />
    </>
  );
};

export const LinkWheelDefaultProps = {
  wheelId: "",
  showDescription: false,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  borderRadius: undefined,
  titleColor: "#123051",
  textColor: "#565556",
  backgroundColor: "#E5ECFA",
  backgroundOpacity: 100,
  borderColor: "suggested_color_0",
  borderWidth: 1,
  dropShadowColor: undefined,
};

LinkWheel.craft = {
  props: LinkWheelDefaultProps,
  related: {
    settings: (props) => (
      <LinkWheelSettings
        {...props}
        wheelId={props.wheelId}
        selectedWheelData={props.wheelData}
      />
    ),
  },
};
